import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { ApiError, apiFetch } from "@/lib/api";
import type { AuthState, User } from "@/auth/types";

/**
 * apps/web :: auth :: AuthContext
 *
 * Single source of truth for "is the user signed in?" inside the React tree.
 * Mounts `GET /auth/me` once at startup, exposes `refresh()` for callers that
 * just returned from the WorkOS redirect, and `logout()` which hits
 * `POST /auth/logout` then flips to anonymous.
 *
 * We deliberately do NOT use React Query for this — auth state is process-
 * global and mutating, so a simple context keeps the mental model small.
 */

interface AuthContextValue {
  state: AuthState;
  /** Re-fetch `/auth/me`. Use after returning from the WorkOS callback. */
  refresh: () => Promise<void>;
  /** Hit `POST /auth/logout`, clear local state; errors are swallowed (best-effort). */
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  /**
   * Skip the initial `GET /auth/me` probe. Only used by tests that want to
   * inject a specific state without triggering a network call in StrictMode's
   * double-invoked effect. Production always leaves this false.
   */
  skipInitialFetch?: boolean;
}

export function AuthProvider({ children, skipInitialFetch = false }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>(
    skipInitialFetch ? { kind: "anonymous" } : { kind: "loading" },
  );
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    try {
      // PR #63 widened `/auth/me` to return the full shared `User` shape
      // verbatim (snake_case per `@SerialName`), so no client-side adapter
      // is needed — the fetch response IS our `User`.
      const user = await apiFetch<User>("/auth/me");
      if (!mountedRef.current) return;
      setState({ kind: "authenticated", user });
    } catch (err) {
      if (!mountedRef.current) return;
      if (err instanceof ApiError && err.status === 401) {
        setState({ kind: "anonymous" });
        return;
      }
      // Network / 5xx: treat as anonymous but don't spam console in tests.
      setState({ kind: "anonymous" });
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch("/auth/logout", { method: "POST" });
    } catch {
      // Best-effort — the cookie is HttpOnly so all we can do is tell the
      // backend to delete its Redis entry. If that fails the cookie's HMAC
      // will still be verified on next request; worst case the user's next
      // `/auth/me` 401s and we land in `anonymous` anyway.
    } finally {
      if (mountedRef.current) {
        setState({ kind: "anonymous" });
      }
    }
  }, []);

  useEffect(() => {
    if (skipInitialFetch) return;
    void refresh();
  }, [refresh, skipInitialFetch]);

  const value = useMemo<AuthContextValue>(
    () => ({ state, refresh, logout }),
    [state, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Read the current auth state from context.
 *
 * Throws if called outside an {@link AuthProvider}; this is intentional —
 * it's always a bug to render an auth-aware component without the provider.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error("useAuth() must be used inside <AuthProvider>");
  }
  return ctx;
}

/**
 * Test-only escape hatch that renders children inside a pre-populated
 * context. Lets unit tests skip the `/auth/me` round-trip and drive UI into
 * any auth state directly.
 */
export function AuthProviderMock({
  state,
  children,
  refresh = async () => undefined,
  logout = async () => undefined,
}: {
  state: AuthState;
  children: ReactNode;
  refresh?: () => Promise<void>;
  logout?: () => Promise<void>;
}) {
  const value = useMemo<AuthContextValue>(
    () => ({ state, refresh, logout }),
    [state, refresh, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
